import axios from 'axios';
import { logger } from '@/shared/utils/logger';
import { isNativeApp } from '@/shared/utils/nativeBridge';
import { isGcsUri, resolveUrl } from '@/shared/utils/signedUrl.util';
import { navigateWithAnchor, saveBlobWithAnchor } from './anchorDownload';
import { downloadBlob } from './downloadBlob';
import { openRemoteUrl } from './openRemoteUrl';

const BLOB_DOWNLOAD_TIMEOUT_MS = 30000;

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/xml': '.xml',
  'text/xml': '.xml',
  'text/csv': '.csv',
  'application/zip': '.zip',
  'text/plain': '.txt',
  'application/x-x509-ca-cert': '.cer',
  'application/pkix-cert': '.cer',
};

type DownloadFileInput = {
  /** An https link, a Google Cloud Storage link or `gs://` URI, or a local `blob:`. Empty is a no-op. */
  url?: string | null;
  /** Name for the saved file in a browser; defaults to the one in the URL. */
  fileName?: string;
  /**
   * The response already carries `Content-Disposition: attachment` (the CSD files of
   * `GET /csd/seal/files`): no signing and no blob fetch, the browser saves it from the
   * current tab.
   */
  attachment?: boolean;
};

/** A file the browser holds in memory (`blob:`), such as the invoice files the Go services return in base64. */
export const isLocalFileUrl = (url: string): boolean => url.startsWith('blob:');

const getHostSafe = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return 'invalid_url';
  }
};

const getFileNameFromUrl = (url: string, contentType?: string): string => {
  let fileName = 'documento';
  try {
    const segments = new URL(url).pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (lastSegment) fileName = decodeURIComponent(lastSegment);
  } catch {
    return fileName;
  }
  if (!/\.[A-Za-z0-9]+$/.test(fileName) && contentType) {
    const extension = CONTENT_TYPE_EXTENSIONS[contentType.split(';')[0].trim()];
    if (extension) fileName += extension;
  }
  return fileName;
};

export const saveRemoteFileAsBlob = async (url: string, fileName?: string): Promise<void> => {
  const response = await axios.get<Blob>(url, {
    responseType: 'blob',
    timeout: BLOB_DOWNLOAD_TIMEOUT_MS,
  });
  saveBlobWithAnchor(
    response.data,
    fileName ?? getFileNameFromUrl(url, response.headers['content-type']),
  );
};

/** Reads a `blob:` file the page holds in memory. */
export const readLocalFile = async (url: string): Promise<Blob> => (await fetch(url)).blob();

const saveLocalFile = async (url: string, fileName?: string): Promise<void> => {
  try {
    const blob = await readLocalFile(url);
    await downloadBlob({ blob, fileName: fileName ?? getFileNameFromUrl(url, blob.type) });
  } catch (error) {
    logger.error('local_file_download', error);
  }
};

const resolveDownloadUrl = async (rawUrl: string): Promise<string | null> =>
  resolveUrl(rawUrl).catch((error) => {
    logger.error('signed_url_resolve', error, { host: getHostSafe(rawUrl) });
    return isGcsUri(rawUrl) ? null : rawUrl;
  });

/**
 * Downloads a remote file (possibly a private Google Cloud Storage document), or a
 * local `blob:` file through `downloadBlob`.
 *
 * Inside the native app a download is a real https URL opened in the system browser
 * (the WebView cannot save files); the bridge makes that safe after an await, and
 * binaries without it are handled by `openRemoteUrl`.
 *
 * In a browser it fetches the resolved URL as a blob and saves it from the current
 * tab. Navigating to the URL instead can't download: the signed URL carries no
 * `content-disposition: attachment`, so browsers render the file inline. If the blob
 * fetch fails (host without CORS for this origin) it falls back to a new tab.
 *
 * If signing fails it falls back to the raw URL, except for a `gs://` URI, which no
 * browser can open: then it only logs. It never throws.
 */
export const downloadFile = async ({
  url: rawUrl,
  fileName,
  attachment = false,
}: DownloadFileInput): Promise<void> => {
  if (!rawUrl) return;
  if (isLocalFileUrl(rawUrl)) return saveLocalFile(rawUrl, fileName);

  const url = attachment ? rawUrl : await resolveDownloadUrl(rawUrl);
  if (!url) return;

  if (isNativeApp()) {
    openRemoteUrl(url);
    return;
  }
  if (attachment) {
    navigateWithAnchor(url);
    return;
  }

  try {
    await saveRemoteFileAsBlob(url, fileName);
  } catch (error) {
    logger.error('signed_url_blob_download', error, { host: getHostSafe(url) });
    openRemoteUrl(url);
  }
};
